import React, { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AddIcon from "@mui/icons-material/Add";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { PurchaseConfirmDialog } from "/imports/ui/components/shared";
import { formatExpiry } from "/imports/ui/views/PackagesView/packagesHelpers";
import ClubBookingCancelDialog from "./ClubBookingCancelDialog";
import NotIncludedSection from "./NotIncludedSection";

const wall = (iso) => DateTime.fromISO(iso, { zone: "utc" });

/**
 * Sticky bottom tray for pack-composition mode on the Lessons / Courts pages:
 * progress bar + picked-session chips (tap ✕ to remove), Repeat weekly, and
 * Continue → confirm → purchase. All state lives in usePackComposer — this is
 * pure presentation shared by both categories.
 */
function PackComposerTray({ pack, onGoToDate }) {
  const [listOpen, setListOpen] = useState(false);
  const blockedWeeks = pack?.blockedWeeks || [];
  const blockedCount = blockedWeeks.length;

  // A hole in the series is the one thing in this list the client has to act
  // on, so a repeat that leaves one opens the list instead of hiding it behind
  // the chevron.
  //
  // Only on a RISE. Tapping a placeholder consumes it, and reopening on the way
  // down would put the list straight back over the slot grid the tap just sent
  // the client to.
  const prevBlockedCount = useRef(0);
  useEffect(() => {
    if (blockedCount > prevBlockedCount.current) setListOpen(true);
    prevBlockedCount.current = blockedCount;
  }, [blockedCount]);

  // The club's lessons are ticked on arrival without the client touching
  // anything, and the snackbar saying so is gone in seconds — open the list so
  // they can see which lessons this pack is about to pay for.
  const existingTicked = !!pack?.existingTicked;
  useEffect(() => {
    if (existingTicked) setListOpen(true);
  }, [existingTicked]);

  if (!pack?.resolved) return null;

  const { pkg, opt, picks, committed, claimedCount, unclaimed, sessionsN, minPicks, mode, currency, effectivePrice, regularPrice } = pack;
  const progress = sessionsN > 0 ? Math.min(100, (committed.length / sessionsN) * 100) : 0;
  const sharedRow = committed.length < sessionsN;
  const sharedRowText = { fontSize: { xs: "0.8125rem", sm: "0.875rem" }, "& .MuiButton-startIcon svg": { fontSize: { xs: "1.125rem", sm: "1.25rem" } } };

  // The purchase and its blocked weeks read as one series, so they are
  // interleaved by date rather than shown as separate lists. A lesson the club
  // already booked sits in the same line of dates as everything else — it is
  // one of the pack's sessions, just one nobody has to pick.
  const rows = [
    ...committed.map((p) => ({ kind: p.claimed ? "claimed" : "pick", sortKey: p.date, pick: p })),
    ...blockedWeeks.map((b) => ({ kind: "blocked", sortKey: b.date, week: b }))
  ].sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));

  const confirmLines = [
    [
      opt.sessions ? `${opt.sessions} session${opt.sessions === 1 ? "" : "s"}` : "Package",
      opt.sessionDuration ? `${opt.sessionDuration} min each` : null,
      formatExpiry(opt)
    ]
      .filter(Boolean)
      .join(" · "),
    `${committed.length} session${committed.length === 1 ? "" : "s"} booked now${
      mode === "partial" && committed.length < sessionsN ? ` — ${sessionsN - committed.length} left to book later (subject to availability)` : ""
    }`,
    // Named separately: these are lessons the client already has, and paying
    // here is what settles them. Nothing new is booked for them.
    claimedCount > 0
      ? `Includes ${claimedCount} lesson${claimedCount === 1 ? "" : "s"} the club already booked for you — this pays for ${
          claimedCount === 1 ? "it" : "them"
        }`
      : null,
    committed.length > 0
      ? `${wall(committed[0].startDate).toFormat("d LLL")} – ${wall(committed[committed.length - 1].startDate).toFormat("d LLL yyyy")}`
      : null
  ].filter(Boolean);

  const confirmNote = pack.anchor === "firstSession" && !opt.noExpiry ? "Your pack's validity starts at your first session, not today." : null;

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          // Clears BottomNav, which is fixed at bottom 0 below md on non-checkout
          // routes and paints at zIndex 1000 — at bottom 0 and zIndex 20 the tray
          // ran under it and took the Continue button out of reach.
          bottom: { xs: "3.5rem", md: 0 },
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          boxShadow: "0 -6px 24px rgba(26,26,26,0.08)"
        }}
      >
        <LinearProgress variant="determinate" value={progress} sx={{ height: "0.25rem" }} />
        <Box sx={{ maxWidth: "60rem", mx: "auto", px: 2, py: 1.25 }}>
          {/* Picked sessions — collapsed by default to keep the tray short. */}
          <Collapse in={listOpen}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pb: 1.25, maxHeight: "35vh", overflowY: "auto" }}>
              {rows.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Nothing picked yet — tap a time to add it.
                </Typography>
              ) : (
                rows.map((row) =>
                  row.kind === "pick" ? (
                    <Chip
                      key={row.pick.key}
                      size="small"
                      label={`${wall(row.pick.startDate).toFormat("ccc d LLL")} · ${row.pick.time}${
                        row.pick.trainerName ? ` · ${row.pick.trainerName}` : ""
                      }`}
                      // Takes the client back to that day to see or change the time.
                      // Folds the list, which would otherwise cover the times.
                      onClick={() => {
                        setListOpen(false);
                        (onGoToDate || pack.goToDate)(row.pick.date);
                      }}
                      onDelete={() => pack.removePick(row.pick.key)}
                    />
                  ) : row.kind === "claimed" ? (
                    // Already on the calendar — the club booked it and never
                    // charged for it, and this pack is what pays for it. Marked
                    // apart from the picks because nothing is being booked here.
                    // ✕ offers to cancel it, so it doesn't sit unpaid on the
                    // trainer's day.
                    <Chip
                      key={row.pick.key}
                      size="small"
                      color="primary"
                      variant="outlined"
                      icon={<EventAvailableIcon />}
                      label={`${wall(row.pick.startDate).toFormat("ccc d LLL")} · ${row.pick.time}${
                        row.pick.trainerName ? ` · ${row.pick.trainerName}` : ""
                      } · already booked`}
                      onDelete={() => pack.requestRemoveClaimed(row.pick.bookingClientId)}
                      sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}
                    />
                  ) : (
                    // Never a real slot — the server never offered this time, so
                    // the chip is a placeholder in the client's series that sends
                    // them to that day to take another one.
                    <Chip
                      key={`blocked-${row.week.date}`}
                      size="small"
                      color="error"
                      variant="outlined"
                      icon={<EventBusyIcon />}
                      label={`${wall(row.week.date).toFormat("ccc d LLL")} · ${row.week.time} · unavailable`}
                      // Folds the list away, because the tap has just sent the
                      // client to that day and the tray was covering the times
                      // they were sent there to pick from. Dismissing with ✕ does
                      // not: they stay in the list to work through the rest.
                      onClick={() => {
                        setListOpen(false);
                        pack.goToBlockedWeek(row.week);
                      }}
                      onDelete={() => pack.dismissBlockedWeek(row.week.date)}
                      sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.08) }}
                    />
                  )
                )
              )}
              {/* An action, not another session — a chip here read as one more
                  item in the series sitting next to the real ones. */}
              {(picks.length > 0 || blockedCount > 0) && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteSweepOutlinedIcon />}
                  onClick={pack.clearPicks}
                  // Sits in a row of small Chips, so it takes their 1.5rem height
                  // rather than a Button's taller default.
                  sx={{
                    textTransform: "none",
                    height: "1.5rem",
                    px: 1,
                    minWidth: 0,
                    fontSize: "0.8125rem",
                    "& .MuiButton-startIcon": { mr: 0.5, ml: 0, "& svg": { fontSize: "1rem" } }
                  }}
                >
                  Clear all
                </Button>
              )}
            </Box>

            {/* Lessons the club booked, inside this pack's validity, that it is
                NOT paying for — ones past the count on arrival, and ones the
                client left out because the club's rules wouldn't let them cancel.
                The slot grid can never offer them: the lesson itself is what
                makes that time unavailable. */}
            <NotIncludedSection count={unclaimed.length} totalChips={rows.length + unclaimed.length}>
              {unclaimed.map((item) => (
                <Chip
                  key={`unclaimed:${item.bookingClientId}`}
                  size="small"
                  variant="outlined"
                  icon={<AddIcon />}
                  label={`${wall(item.startDate).toFormat("ccc d LLL")} · ${item.time}${item.trainerName ? ` · ${item.trainerName}` : ""}`}
                  onClick={() => pack.restoreClaimed(item)}
                  onDelete={() => pack.requestCancelUnclaimed(item)}
                  sx={{ color: "text.secondary", borderStyle: "dashed" }}
                />
              ))}
            </NotIncludedSection>
          </Collapse>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1, sm: 1.5 }
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {pkg.name} — {committed.length} of {sessionsN} sessions
                </Typography>
                {blockedCount > 0 ? (
                  <Typography variant="caption" color="error.main" noWrap>
                    {blockedCount === 1 ? "1 week unavailable" : `${blockedCount} weeks unavailable`} — tap it to pick another time
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {regularPrice > effectivePrice && (
                      <Box component="span" sx={{ mr: 0.75, fontWeight: 400, textDecoration: "line-through" }}>
                        {currency} {regularPrice.toFixed(2)}
                      </Box>
                    )}
                    {currency} {effectivePrice.toFixed(2)}
                    {mode === "partial" && committed.length < sessionsN ? " · book all to guarantee your spots" : ""}
                  </Typography>
                )}
                {/* Where the sessions must stay for the member price — it moves as they do. */}
                {pack.memberNote && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: pack.memberNote.warning ? "warning.dark" : "primary.main", fontWeight: 600 }}
                  >
                    {pack.memberNote.text}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => setListOpen((o) => !o)}
                aria-label={listOpen ? "Hide picked sessions" : "Show picked sessions"}
                // Mobile stacks the summary onto its own row, where a chevron
                // sitting against the text reads as part of the sentence. Pinned
                // to the row's edge it reads as the control for the row.
                sx={{ ml: { xs: "auto", sm: 0 }, flexShrink: 0 }}
              >
                {listOpen ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* No size prop: it shares the row with Continue, and a small
                  button beside a medium one sat visibly short of it. */}
              {sharedRow && (
                <Button
                  variant="outlined"
                  startIcon={pack.repeatBusy ? <CircularProgress size="1rem" /> : <EventRepeatIcon />}
                  disabled={committed.length === 0 || pack.repeatBusy}
                  onClick={pack.repeatWeekly}
                  sx={{ textTransform: "none", flexShrink: 0, ...sharedRowText }}
                >
                  Repeat weekly
                </Button>
              )}
              <Button
                variant="contained"
                disabled={!pack.canContinue || pack.buying}
                onClick={() => pack.setConfirmOpen(true)}
                sx={{ textTransform: "none", color: "common.white", flex: { xs: 1, sm: "0 0 auto" }, minWidth: { sm: "10rem" }, ...(sharedRow && sharedRowText) }}
              >
                {/* An unopened hole outranks the count: a partial pack can be
                    paid for with picks missing, but never without the client
                    having looked at the week that could not be booked. */}
                {blockedCount > 0
                  ? blockedCount === 1
                    ? "Check 1 unavailable week"
                    : `Check ${blockedCount} unavailable weeks`
                  : committed.length < minPicks
                    ? mode === "all"
                      ? `Pick ${sessionsN - committed.length} more`
                      : "Pick at least 1 session"
                    : "Continue to payment"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <ClubBookingCancelDialog cancel={pack.clubCancel} />

      <PurchaseConfirmDialog
        open={pack.confirmOpen}
        name={pkg?.name}
        lines={confirmLines}
        note={confirmNote}
        notices={pack.packRules}
        location={null}
        price={effectivePrice}
        regularPrice={regularPrice}
        currency={currency || ""}
        wallets={pack.wallets}
        loading={pack.buying}
        onConfirm={pack.doPurchase}
        onClose={() => pack.setConfirmOpen(false)}
      />
    </>
  );
}

export default PackComposerTray;
